import { useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useForm, Controller, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Modal from '@/components/common/modal';
import { useCreateSubscriptionPlanMutation } from '@/state/mutations/admin-subscription-plans/use-create-subscription-plan-mutation';
import { useUpdateSubscriptionPlanMutation } from '@/state/mutations/admin-subscription-plans/use-update-subscription-plan-mutation';
import type { SubscriptionCategory, SubscriptionPlan } from '@/types/dtos/transactions/subscription-plan.dto';
import { SUBSCRIPTION_CATEGORY, SUBSCRIPTION_TIER } from '@/types/dtos/transactions/subscription-plan.dto';
import { defaultMainSubscriptionProductKeyForCategory } from '@/helpers/organization-plan-catalog';
import { useTranslations } from '@/i18n/use-translations';

const emptyCategory = '' as const;

const subscriptionCategoryZ = z.union([
  z.enum(SUBSCRIPTION_CATEGORY as unknown as [SubscriptionCategory, ...SubscriptionCategory[]]),
  z.literal(''),
]);

const schema = z.object({
  tier: z.enum(['FREE', 'BASIC', 'SMART', 'PRO', 'PREMIUM']),
  entityType: z.enum(['Organization', 'Event']),
  billingPeriod: z.enum(['MONTH', 'YEAR', 'ONCE']),
  price: z.coerce.number().nonnegative(),
  subscriptionProductKey: z.string().optional(),
  subscriptionCategory: subscriptionCategoryZ,
  requiresParent: z.boolean(),
  parentProductKey: z.string().optional(),
  minParentTier: z.union([z.enum(['FREE', 'BASIC', 'SMART', 'PRO', 'PREMIUM']), z.literal('')]),
  includedQuotasJson: z.string().optional(),
  maxQuotasJson: z.string().optional(),
  overagePricesJson: z.string().optional(),
  featureKeys: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

/** Zod `z.coerce.number()` output typing can disagree with RHF `Resolver`; keep form values strongly typed. */
const resolver = zodResolver(schema) as Resolver<FormValues>;

/** Example: namespaced quota keys (org host line). */
const defaultIncluded = '[\n  { "quotaKey": "host_default:members", "quantity": 5 }\n]';
const defaultMaxQuotas = '[\n  { "quotaKey": "host_default:members", "quantity": 10 }\n]';
const defaultOverage = '[\n  { "quotaKey": "host_default:members", "pricePerUnit": 5 }\n]';

function parseJsonArray<T>(raw: string | undefined, label: string): T[] {
  const t = (raw ?? '').trim();
  if (!t) {
    return [];
  }
  try {
    const v = JSON.parse(t) as unknown;
    if (!Array.isArray(v)) {
      throw new Error('not an array');
    }
    return v as T[];
  } catch {
    throw new Error(`${label}: invalid JSON array`);
  }
}

export default function SubscriptionPlanAdminFormModal({
  open,
  onClose,
  plan,
}: {
  open: boolean;
  onClose: () => void;
  plan: SubscriptionPlan | null;
}) {
  const t = useTranslations();
  const createMut = useCreateSubscriptionPlanMutation();
  const updateMut = useUpdateSubscriptionPlanMutation();
  const editing = !!plan?.id;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver,
    defaultValues: {
      tier: 'BASIC',
      entityType: 'Organization',
      billingPeriod: 'MONTH',
      price: 0,
      subscriptionProductKey: '',
      subscriptionCategory: emptyCategory,
      requiresParent: false,
      parentProductKey: '',
      minParentTier: emptyCategory,
      includedQuotasJson: defaultIncluded,
      maxQuotasJson: '[]',
      overagePricesJson: defaultOverage,
      featureKeys: '',
    },
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    if (plan) {
      const cat = plan.subscriptionCategory;
      const knownCat =
        cat && (SUBSCRIPTION_CATEGORY as readonly string[]).includes(cat)
          ? cat
          : String(cat) === 'SERVICE_PROVIDER'
            ? 'OTHER'
            : emptyCategory;
      reset({
        tier: plan.tier,
        entityType: plan.entityType as 'Organization' | 'Event',
        billingPeriod: (plan.billingPeriod ?? 'MONTH') as 'MONTH' | 'YEAR' | 'ONCE',
        price: plan.price,
        subscriptionProductKey: plan.subscriptionProductKey ?? '',
        subscriptionCategory: (knownCat === emptyCategory
          ? emptyCategory
          : (knownCat as (typeof SUBSCRIPTION_CATEGORY)[number])) as FormValues['subscriptionCategory'],
        requiresParent: !!plan.requiresParent,
        parentProductKey: plan.parentProductKey ?? '',
        minParentTier: (plan.minParentTier ?? emptyCategory) as
          | 'FREE'
          | 'BASIC'
          | 'SMART'
          | 'PRO'
          | 'PREMIUM'
          | typeof emptyCategory,
        includedQuotasJson: JSON.stringify(plan.includedQuotas ?? [], null, 2),
        maxQuotasJson: JSON.stringify(plan.maxQuotas ?? [], null, 2),
        overagePricesJson: JSON.stringify(plan.overagePrices ?? [], null, 2),
        featureKeys: (plan.featureKeys ?? []).join(', '),
      });
    } else {
      reset({
        tier: 'BASIC',
        entityType: 'Organization',
        billingPeriod: 'MONTH',
        price: 0,
        subscriptionProductKey: '',
        subscriptionCategory: emptyCategory,
        requiresParent: false,
        parentProductKey: '',
        minParentTier: emptyCategory,
        includedQuotasJson: defaultIncluded,
        maxQuotasJson: defaultMaxQuotas,
        overagePricesJson: defaultOverage,
        featureKeys: '',
      });
    }
  }, [open, plan, reset]);

  const requiresParent = watch('requiresParent');
  const entityTypeWatch = watch('entityType');
  const subscriptionCategoryWatch = watch('subscriptionCategory');

  useEffect(() => {
    if (entityTypeWatch !== 'Organization' || requiresParent) {
      return;
    }
    const cat = subscriptionCategoryWatch;
    if (!cat || String(cat) === '') {
      return;
    }
    setValue('subscriptionProductKey', defaultMainSubscriptionProductKeyForCategory(cat as SubscriptionCategory));
  }, [entityTypeWatch, subscriptionCategoryWatch, requiresParent, setValue]);

  // Org add-ons: suggest parent from category; Event add-ons keep a free-text parent key.
  useEffect(() => {
    if (!requiresParent || entityTypeWatch !== 'Organization') {
      return;
    }
    const cat = subscriptionCategoryWatch;
    if (!cat || String(cat) === '') {
      return;
    }
    setValue('parentProductKey', defaultMainSubscriptionProductKeyForCategory(cat as SubscriptionCategory));
  }, [requiresParent, entityTypeWatch, subscriptionCategoryWatch, setValue]);

  const onSubmit = async (values: FormValues) => {
    let includedQuotas: { quotaKey: string; quantity: number }[];
    let maxQuotas: { quotaKey: string; quantity: number }[];
    let overagePrices: { quotaKey: string; pricePerUnit: number }[];
    try {
      includedQuotas = parseJsonArray(values.includedQuotasJson, 'Included quotas');
      maxQuotas = parseJsonArray(values.maxQuotasJson ?? '', 'Max quotas');
      overagePrices = parseJsonArray(values.overagePricesJson, 'Overage prices');
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Invalid JSON');
      return;
    }
    const featureKeys = values.featureKeys
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const pk = values.subscriptionProductKey?.trim();
    const resolvedCategory =
      values.subscriptionCategory && String(values.subscriptionCategory) !== emptyCategory
        ? (values.subscriptionCategory as SubscriptionCategory)
        : undefined;
    const parPk = values.requiresParent ? values.parentProductKey?.trim() || undefined : undefined;
    if (values.requiresParent && !parPk) {
      window.alert(t('admin.subscriptionPlansCatalog.parentProductKeyRequired'));
      return;
    }
    if (values.requiresParent && values.entityType === 'Organization' && !resolvedCategory) {
      window.alert(t('admin.subscriptionPlansCatalog.addOnRequiresCategory'));
      return;
    }
    const body = {
      tier: values.tier,
      entityType: values.entityType,
      billingPeriod: values.billingPeriod,
      price: values.price,
      // Preserve legacy `features.members` — empty `{}` is stripped by Mongoose minimize and fails required validation.
      features: plan?.features && Object.keys(plan.features).length > 0 ? plan.features : undefined,
      includedQuotas,
      maxQuotas: maxQuotas.length ? maxQuotas : undefined,
      overagePrices,
      featureKeys: featureKeys?.length ? featureKeys : undefined,
      subscriptionProductKey: pk || undefined,
      subscriptionCategory: (() => {
        const v = values.subscriptionCategory as string;
        return v && v !== emptyCategory ? (v as (typeof SUBSCRIPTION_CATEGORY)[number]) : undefined;
      })(),
      /** Always send so PATCH can set `false` and drop add-on gating. */
      requiresParent: values.requiresParent,
      parentProductKey: parPk,
      minParentTier: (() => {
        const v = values.minParentTier as string;
        if (!values.requiresParent || !v || v === emptyCategory) {
          return undefined;
        }
        return v as 'FREE' | 'BASIC' | 'SMART' | 'PRO' | 'PREMIUM';
      })(),
    };
    try {
      if (editing && plan?.id) {
        await updateMut.mutateAsync({ id: plan.id, body });
      } else {
        await createMut.mutateAsync(body);
      }
      onClose();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const pending = createMut.isPending || updateMut.isPending;

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={editing ? t('admin.subscriptionPlansCatalog.titleEdit') : t('admin.subscriptionPlansCatalog.titleCreate')}
      description={t('admin.subscriptionPlansCatalog.modalDescription')}
      size="lg"
      contentSx={{ maxHeight: 'min(80vh, 640px)', overflowY: 'auto' }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            name="tier"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel>Tier</InputLabel>
                <Select label="Tier" {...field}>
                  {SUBSCRIPTION_TIER.map((tierValue) => (
                    <MenuItem key={tierValue} value={tierValue}>
                      {tierValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          <Controller
            name="entityType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel>Entity</InputLabel>
                <Select label="Entity" {...field}>
                  <MenuItem value="Organization">Organization</MenuItem>
                  <MenuItem value="Event">Event</MenuItem>
                </Select>
              </FormControl>
            )}
          />
          <Controller
            name="billingPeriod"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small">
                <InputLabel>Billing period</InputLabel>
                <Select label="Billing period" {...field}>
                  <MenuItem value="MONTH">Monthly</MenuItem>
                  <MenuItem value="YEAR">Yearly</MenuItem>
                  <MenuItem value="ONCE">Once (non-renewing)</MenuItem>
                </Select>
              </FormControl>
            )}
          />
        </Stack>
        <Controller
          name="price"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Price (€)"
              type="number"
              size="small"
              fullWidth
              helperText="Bundle price per billing period (subscriptions, renewals, checkout)"
            />
          )}
        />
        <Typography variant="subtitle2" color="text.secondary">
          {t('admin.subscriptionPlansCatalog.sectionCategoryFirst')}
        </Typography>
        <Controller
          name="subscriptionCategory"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth size="small">
              <InputLabel>{t('admin.subscriptionPlansCatalog.category')}</InputLabel>
              <Select
                label={t('admin.subscriptionPlansCatalog.category')}
                {...field}
                value={field.value ?? emptyCategory}
              >
                <MenuItem value="">{t('admin.subscriptionPlansCatalog.categoryNone')}</MenuItem>
                {SUBSCRIPTION_CATEGORY.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t('admin.subscriptionPlansCatalog.requiresParent')}
          </Typography>
          <Stack direction="row" flexWrap="wrap" alignItems="center" spacing={1.5} useFlexGap>
            <Controller
              name="requiresParent"
              control={control}
              render={({ field }) => (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={field.value ? 'ADDON' : 'MAIN'}
                  onChange={(_, v) => {
                    if (v === null) {
                      return;
                    }
                    const isAddon = v === 'ADDON';
                    field.onChange(isAddon);
                    if (!isAddon) {
                      setValue('minParentTier', emptyCategory);
                      setValue('parentProductKey', '');
                    }
                  }}
                >
                  <ToggleButton value="MAIN">{t('admin.subscriptionPlansCatalog.planKindMain')}</ToggleButton>
                  <ToggleButton value="ADDON">{t('admin.subscriptionPlansCatalog.planKindAddOn')}</ToggleButton>
                </ToggleButtonGroup>
              )}
            />
            {requiresParent ? (
              <Controller
                name="minParentTier"
                control={control}
                render={({ field }) => (
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>{t('admin.subscriptionPlansCatalog.minParentTier')}</InputLabel>
                    <Select
                      label={t('admin.subscriptionPlansCatalog.minParentTier')}
                      {...field}
                      value={field.value ?? emptyCategory}
                    >
                      <MenuItem value="">{t('admin.subscriptionPlansCatalog.tierNoMinimum')}</MenuItem>
                      {SUBSCRIPTION_TIER.map((tierValue) => (
                        <MenuItem key={tierValue} value={tierValue}>
                          {tierValue}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            ) : null}
          </Stack>
          {requiresParent ? (
            <Controller
              name="parentProductKey"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('admin.subscriptionPlansCatalog.parentProductKey')}
                  placeholder={entityTypeWatch === 'Event' ? 'base_capacity' : 'host_default'}
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                  helperText={
                    entityTypeWatch === 'Organization'
                      ? t('admin.subscriptionPlansCatalog.parentProductKeyHelpOrg')
                      : t('admin.subscriptionPlansCatalog.parentProductKeyHelpEvent')
                  }
                />
              )}
            />
          ) : null}
        </Stack>
        <Controller
          name="subscriptionProductKey"
          control={control}
          render={({ field }) => {
            const lockMainKey =
              entityTypeWatch === 'Organization' &&
              !requiresParent &&
              !!subscriptionCategoryWatch &&
              String(subscriptionCategoryWatch) !== '';
            return (
              <TextField
                {...field}
                label={t('admin.subscriptionPlansCatalog.productKey')}
                placeholder="host_default"
                size="small"
                fullWidth
                disabled={lockMainKey}
                helperText={
                  lockMainKey
                    ? t('admin.subscriptionPlansCatalog.productKeyLockedHelp')
                    : t('admin.subscriptionPlansCatalog.productKeyHelp')
                }
              />
            );
          }}
        />
        <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('admin.subscriptionPlansCatalog.sectionEntitlements')}
        </Typography>
        <Controller
          name="featureKeys"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Commercial feature keys"
              placeholder="e.g. analytics, advancedBranding"
              size="small"
              fullWidth
              helperText="Comma-separated keys from the commercial-features catalog (on/off entitlements; use quotas below for numeric limits)."
            />
          )}
        />
        <Typography variant="subtitle2" color="text.secondary">
          Quotas & overage
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1 }}>
          Numeric limits and per-unit overage belong here—not under “features”.
        </Typography>
        <Controller
          name="includedQuotasJson"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Included quotas (JSON array)"
              multiline
              minRows={4}
              size="small"
              fullWidth
              error={!!errors.includedQuotasJson}
              helperText={
                errors.includedQuotasJson?.message ??
                '[{ "quotaKey": "host_default:members", "quantity": 5 }] — namespaced quota keys and included units per period'
              }
            />
          )}
        />
        <Controller
          name="maxQuotasJson"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Max quotas (JSON array, optional)"
              multiline
              minRows={3}
              size="small"
              fullWidth
              error={!!errors.maxQuotasJson}
              helperText={
                errors.maxQuotasJson?.message ??
                '[{ "quotaKey": "host_default:members", "quantity": 10 }] — optional hard cap on total units per quota for this plan (UI + checkout preview)'
              }
            />
          )}
        />
        <Controller
          name="overagePricesJson"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Overage prices (JSON array)"
              multiline
              minRows={3}
              size="small"
              fullWidth
              error={!!errors.overagePricesJson}
              helperText={
                errors.overagePricesJson?.message ??
                '[{ "quotaKey": "host_default:members", "pricePerUnit": 5, "step": 1 }] — price per extra unit; optional step = UI +/- click size'
              }
            />
          )}
        />
        <Typography variant="caption" color="text.secondary">
          {t('admin.subscriptionPlansCatalog.uniquenessFootnote')}
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button type="button" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="contained" loading={pending}>
            {editing ? t('common.save') : t('common.create')}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}
